import { getProposedPage, proposedGet } from '@/api/proposedEndpoint'
import { CATEGORY_PARAM } from '@/features/news/filters'
import { displayDate, displayTime, pageTotal } from '@/features/market/api/marketMappers'
import {
  newsArticlePageSchema,
  newsArticleSchema,
  newsArticlesQuerySchema,
  type NewsArticle,
  type NewsArticlePage,
  type NewsArticlesQuery,
  type NewsLang,
} from '@/schemas/market-news'

const PAGE_SIZE = 10

/** The wire shapes proposed in `asks/nw1.md`. */
interface NewsArticleSummaryWire {
  id: string
  title: string
  summary: string
  source: string
  /** ISO 8601 with the Kathmandu offset. */
  published_at: string
  thumbnail_url: string | null
  symbol: string | null
}

interface NewsArticleWire extends NewsArticleSummaryWire {
  lang: NewsLang
  byline: string
  hero_image_url: string | null
  summary_paragraphs: string[]
  body: string[]
  note: { title: string; body: string } | null
  source_url: string
  reader_url: string | null
}

const AD_DAY = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'Asia/Kathmandu',
})

/** "1 Ashwin 2083, 3:10 PM". */
const publishedLabel = (iso: string): string => `${displayDate(iso)}, ${displayTime(iso)}`

const domainOf = (url: string): string => new URL(url).hostname.replace(/^www\./, '')

/**
 * Market › News: the news sites' stories, one story, and the page the reader panel frames.
 *
 * `available` is the gate, as `basketsService.available` is: no request and the generic "not live
 * yet" state until mm-be ships `GET /api/v5/market/news/` and `GET /api/v5/market/news/{id}/`
 * (proposed contract in `mm-market-2026-10-28/asks/nw1.md`), then one constant to flip.
 */
export const marketNewsService = {
  available: false as boolean,

  stories: async (input: NewsArticlesQuery, cursor?: string): Promise<NewsArticlePage> => {
    const query = newsArticlesQuerySchema.parse(input)
    const pageNumber = cursor === undefined ? 1 : Number(cursor)
    const page = await getProposedPage<NewsArticleSummaryWire>('/api/v5/market/news/', {
      page: pageNumber,
      page_size: PAGE_SIZE,
      sort: query.sort,
      lang: query.lang,
      ...(query.scope === undefined || query.scope === 'all'
        ? {}
        : { category: CATEGORY_PARAM[query.scope] }),
      ...(query.query === undefined ? {} : { q: query.query }),
      ...(query.show === undefined ? {} : { show: query.show }),
      ...(query.sectors === undefined ? {} : { sector: query.sectors.join(',') }),
      ...(query.sources === undefined ? {} : { source: query.sources.join(',') }),
    })
    const total = pageTotal(page.meta, page.rows.length)
    return newsArticlePageSchema.parse({
      rows: page.rows.map((row) => ({
        id: row.id,
        title: row.title,
        summary: row.summary,
        source: row.source,
        publishedLabel: publishedLabel(row.published_at),
        ...(row.thumbnail_url === null ? {} : { thumbnailUrl: row.thumbnail_url }),
        ...(row.symbol === null ? {} : { symbol: row.symbol }),
      })),
      ...(pageNumber * PAGE_SIZE < total ? { nextCursor: String(pageNumber + 1) } : {}),
      total,
    })
  },

  article: async (id: string, lang: NewsLang): Promise<NewsArticle> => {
    const row = await proposedGet<NewsArticleWire>(
      `/api/v5/market/news/${encodeURIComponent(id)}/`,
      { lang },
    )
    return newsArticleSchema.parse({
      id: row.id,
      lang: row.lang,
      title: row.title,
      source: row.source,
      byline: row.byline,
      publishedLabel: publishedLabel(row.published_at),
      publishedLine: `Published ${publishedLabel(row.published_at)} (${AD_DAY.format(new Date(row.published_at))})`,
      ...(row.hero_image_url === null ? {} : { heroImageUrl: row.hero_image_url }),
      summary: row.summary_paragraphs,
      body: row.body,
      ...(row.symbol === null ? {} : { symbol: row.symbol }),
      ...(row.note === null ? {} : { note: row.note }),
      sourceUrl: row.source_url,
      sourceDomain: domainOf(row.source_url),
      ...(row.reader_url === null ? {} : { readerUrl: row.reader_url }),
    })
  },
}
